export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface ParsedGooglePlaceAddress {
  street: string;
  postal_code: string;
  city: string;
  country: string;
  formatted: string;
  lat: number | null;
  lng: number | null;
  place_id?: string;
}

type LatLngLike =
  | { lat: () => number; lng: () => number }
  | { lat: number; lng: number };

export interface GooglePlaceLike {
  formatted_address?: string;
  address_components?: GoogleAddressComponent[];
  geometry?: { location?: LatLngLike };
  place_id?: string;
}

function findComponent(components: GoogleAddressComponent[], type: string) {
  return components.find((c) => c.types.includes(type));
}

/** LatLng Google (méthodes) ou littéral — évite `loc.lat is not a function` dans les callbacks Places. */
export function readGoogleLatLng(
  location: LatLngLike | undefined | null,
): { lat: number | null; lng: number | null } {
  if (!location) return { lat: null, lng: null };
  const latRaw = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lngRaw = typeof location.lng === 'function' ? location.lng() : location.lng;
  return {
    lat: typeof latRaw === 'number' && Number.isFinite(latRaw) ? latRaw : null,
    lng: typeof lngRaw === 'number' && Number.isFinite(lngRaw) ? lngRaw : null,
  };
}

function readLatLng(location: LatLngLike | undefined): { lat: number | null; lng: number | null } {
  return readGoogleLatLng(location);
}

function buildStreet(components: GoogleAddressComponent[]): string {
  const number = findComponent(components, 'street_number')?.long_name?.trim() || '';
  const route = findComponent(components, 'route')?.long_name?.trim() || '';
  return [number, route].filter(Boolean).join(' ').trim();
}

function buildCity(components: GoogleAddressComponent[]): string {
  return (
    findComponent(components, 'locality')?.long_name ||
    findComponent(components, 'postal_town')?.long_name ||
    findComponent(components, 'administrative_area_level_2')?.long_name ||
    ''
  ).trim();
}

/** Shared Places parse for client profile address (#561). */
export function parseGooglePlaceAddress(place: GooglePlaceLike | null | undefined): ParsedGooglePlaceAddress {
  const components = place?.address_components || [];
  const formatted = (place?.formatted_address || '').trim();
  const streetFromComponents = buildStreet(components);
  const postal_code = (findComponent(components, 'postal_code')?.long_name || '').trim();
  const city = buildCity(components);
  const { lat, lng } = readLatLng(place?.geometry?.location);

  // City-level Places: formatted embeds CP+ville — do not use as street
  // (parity BO parseGooglePlaceAddress / ManualInvoice freeText rebuild).
  let street = streetFromComponents;
  if (!street && formatted) {
    const hay = formatted.toLowerCase();
    const embedsPostal = Boolean(postal_code && hay.includes(postal_code.toLowerCase()));
    const embedsCity = Boolean(city && hay.includes(city.toLowerCase()));
    if (!(embedsPostal || embedsCity)) {
      street = formatted;
    }
  }

  return {
    street,
    postal_code,
    city,
    country: (findComponent(components, 'country')?.short_name || '').trim(),
    formatted,
    lat,
    lng,
    place_id: place?.place_id,
  };
}
